import React, { useState, useEffect, useRef } from 'react';
import IconCalendar from './IconCalendar';
import IconChevron from './IconChevron';
import DatePickerBody from './DatePickerBody';
import moment from 'moment';

const DatePicker = ({
    currentDate,
    onChange,
    disabled = false,
    inputStyle,
    buttonStyle,
    width,
    dateFormat = 'MMM Do YYYY',
    useStrings = false,
    showPrevNextButtons = false,
    placeholder,
    containerStyle,
    hideIcon = false,
    ...props
}) => {
    // Set Monday as first day of week
    moment.locale('en-gb', {
        week: {
            dow: 1
        },
        weekdaysShort: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'],
        weekdays: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'],
        weekdaysMin: ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su']
    });

    const [showingBody, setShowingBody] = useState(false);
    const [dateMoment, setDateMoment] = useState(moment());
    const [isPrevNextButtonsDisabled, setIsPrevNextButtonsDisabled] = useState(false);
    const calendarButtonRef = useRef(null);
    const calendarBodyRef = useRef(null);

    const groupStyle = {
        position: 'relative',
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'stretch',
        width: width || '100%',
        ...props.style
    };

    const disabledCalendarStyle = {
        backgroundColor: '#F7F7F7',
        color: '#414141'
    };

    const disabledButtonStyle = {
        backgroundColor: '#CCCCCC',
        border: '1px solid #A6A6A6'
    };

    const calendarStyle = {
        boxSizing: 'border-box',
        height: 40,
        border: '1px solid #D7D7D7',
        borderRadius: '8px 0 0 8px',
        backgroundColor: '#FFFDF4',
        color: moment(currentDate).isValid() ? '#003A98' : '#CCCCCC',
        fontFamily: 'Roboto',
        fontSize: 15,
        fontWeight: moment(currentDate).isValid() ? 600 : 400,
        verticalAlign: 'middle',
        textAlign: 'left',
        letterSpacing: 0,
        paddingLeft: 11,
        cursor: disabled ? 'default' : 'pointer',
        width: hideIcon ? '90%' : '70%',
        ...inputStyle,
        ...(disabled ? disabledCalendarStyle : {})
    };

    const defaultButtonStyle = {
        boxSizing: 'border-box',
        height: 40,
        border: '1px solid #FD9A00',
        borderRadius: '0 5px 5px 0',
        backgroundColor: '#FAA800',
        width: '20%',
        cursor: disabled ? 'default' : 'pointer',
        ...buttonStyle,
        ...(disabled ? disabledButtonStyle : {})
    };

    useEffect(() => {
        let theMoment = moment(currentDate);

        if (useStrings) {
            // If a date object assigned to currentDate, the time offset can create an issue.
            // In BST time zone, '2021-04-06T00:00:00 GMT +01:00' gets converted to '2021-04-05T23:00:00:00Z'.
            // To avoid it, create and use UTC time from date element of the date object.
            if (typeof currentDate !== 'string') {
                theMoment = moment.utc(currentDate.toDateString()); // toDateString() returns only date element.

                if (onChange) {
                    const event = {
                        target: {
                            type: 'change',
                            id: props.id,
                            name: props.name,
                            value: theMoment.toDate().toISOString() // 2021-04-06T00:00:00.000Z; The value is separated from a date format.
                        },
                        currentTarget: {
                            type: 'change',
                            id: props.id,
                            name: props.name,
                            value: theMoment.toDate().toISOString()
                        }
                    };

                    onChange(event); // Update the caller's state with the ISO format string date.
                }
            }
        }

        if (theMoment.isValid()) {
            setDateMoment(theMoment);
        }
    }, [currentDate]);

    const handleCalendarClick = () => {
        if (disabled) {
            return;
        }
        setIsPrevNextButtonsDisabled(!isPrevNextButtonsDisabled);
        setShowingBody(!showingBody);
    };

    const handleClickOutside = (event) => {
        if (!calendarBodyRef.current.contains(event.target)) {
            setShowingBody(false);
        }
    };

    const updateDate = (date, isShowingBody) => {
        setDateMoment(date);
        setShowingBody(isShowingBody);

        const nativeObject = date.toDate();
        const minutes = nativeObject.getMinutes();
        const offsetMinutes = nativeObject.getTimezoneOffset();

        // Compensate timezone offset to store local time in the database.
        nativeObject.setMinutes(minutes - offsetMinutes);

        if (onChange) {
            // This is a fudge to mimic an event although it is nowhere complete and there has to be a better way...
            const event = {
                target: {
                    type: 'change',
                    id: props.id,
                    name: props.name,
                    value: useStrings ? nativeObject.toISOString() : nativeObject
                },
                currentTarget: {
                    type: 'change',
                    id: props.id,
                    name: props.name,
                    value: useStrings ? nativeObject.toISOString() : nativeObject
                }
            };

            onChange(event);
        }
    };

    const handleUpdateDate = (date) => {
        updateDate(date, !showingBody);
    };

    const handleUpdateDateHideBody = (date) => {
        updateDate(date, false);
    };

    const handlePrev = () => {
        handleUpdateDateHideBody(moment(currentDate).subtract(1, 'days'));
    };

    const handleNext = () => {
        handleUpdateDateHideBody(moment(currentDate).add(1, 'days'));
    };

    const prevIcon = showPrevNextButtons ? (
        <IconChevron
            id={'PrevElement'}
            width={18}
            height={18}
            style={{
                float: 'left',
                display: 'inline',
                padding: '14px 14px 7px 14px',
                transform: 'rotate(90deg)',
                position: 'absolute',
                left: '-5%'
            }}
            disabled={isPrevNextButtonsDisabled}
            onClick={!isPrevNextButtonsDisabled ? handlePrev : null}
        />
    ) : (
        <div></div>
    );

    const nextIcon = showPrevNextButtons ? (
        <IconChevron
            id={'NextElement'}
            width={18}
            height={18}
            style={{
                display: 'inline',
                padding: '14px 14px 7px 14px',
                transform: 'rotate(270deg)',
                position: 'absolute',
                right: '25%'
            }}
            disabled={isPrevNextButtonsDisabled}
            onClick={!isPrevNextButtonsDisabled ? handleNext : null}
        />
    ) : (
        <div></div>
    );

    return (
        <div id={`${props.id}ParentContainer`} style={{ position: 'relative', ...containerStyle }}>
            <div id={`${props.id}ElementGroup`} ref={calendarBodyRef} style={groupStyle}>
                {prevIcon}
                <input
                    id={`${props.id}Input`}
                    style={{
                        ...calendarStyle,
                        textAlign: showPrevNextButtons ? 'center' : 'left'
                    }}
                    type='button'
                    value={
                        currentDate
                            ? moment(currentDate).isValid()
                                ? moment.utc(currentDate).format(dateFormat)
                                : currentDate
                            : placeholder
                    }
                    onClick={handleCalendarClick}
                    disabled={disabled}
                />
                {nextIcon}
                {!hideIcon &&
                    <div style={{ display: 'contents' }}>
                        <button
                            id={`${props.id}Button`}
                            ref={calendarButtonRef}
                            style={defaultButtonStyle}
                            onClick={handleCalendarClick}
                            disabled={disabled}
                        >
                            <div id={`${props.id}SVGContainer`}>
                                <IconCalendar
                                    style={{
                                        width: '100%',
                                        height: '24px',
                                        marginBottom: 0
                                    }}
                                />
                            </div>
                        </button>
                    </div>
                }
            </div>
            <DatePickerBody
                currentDate={dateMoment}
                onDateUpdate={handleUpdateDate}
                id={props.id}
                headStyle={props.headStyle}
                headTextStyle={props.headTextStyle}
                weekdayStyle={props.weekdayStyle}
                weekendStyle={props.weekendStyle}
                todayStyle={props.todayStyle}
                dayStyle={props.dayStyle}
                blankStyle={props.blankStyle}
                bodyContainerStyle={props.bodyContainerStyle}
                bodyStyle={props.bodyStyle}
                tableStyle={props.tableStyle}
                minDate={props.minDate}
                maxDate={props.maxDate}
                boundingRect={calendarBodyRef?.current?.getBoundingClientRect()}
                expanded={showingBody}
                onClickOutside={handleClickOutside}
            />
        </div>
    );
};

export default DatePicker;
